import React from 'react';
import { Alert, Col, Row, Spin, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  filtersQueryKey,
  getSalesAnalyticsOrders,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';

const { Title } = Typography;

export const SalesAnalyticsOrdersTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['sales-analytics', 'orders', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsOrders(filters),
  });

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const printRows = d.orders_at_risk.map((r) => ({
    sales_order_number: r.sales_order_number,
    customer: r.customer_name,
    required_date: r.required_date,
    readiness_status: r.readiness_status,
  }));

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <a onClick={() => navigate(`/growth/dashboard?tab=operations&section=delivery-queues`)}>
            View delivered-not-invoiced on Operations board →
          </a>
        }
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Orders &amp; fulfillment
        </Title>
        <SalesAnalyticsToolbar
          tab="orders"
          tabTitle="Orders & Fulfillment"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'sales_order_number', label: 'SO #' },
            { key: 'customer', label: 'Customer' },
            { key: 'required_date', label: 'Required' },
            { key: 'readiness_status', label: 'Status' },
          ]}
          printRows={printRows}
          printKpiLines={[
            { label: 'Open backlog', value: formatCurrency(d.open_backlog.open_value) },
            { label: 'DNI estimate', value: formatCurrency(d.delivered_not_invoiced.estimated_net_value) },
          ]}
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Statistic title="Open backlog orders" value={d.open_backlog.order_count} />
          <Statistic title="Backlog value" value={formatCurrency(d.open_backlog.open_value)} />
        </Col>
        <Col xs={24} md={8}>
          <Statistic title="Deliveries posted" value={d.deliveries_posted.count} />
          <Statistic title="Delivery-linked SO value" value={formatCurrency(d.deliveries_posted.value)} />
        </Col>
        <Col xs={24} md={8}>
          <Statistic title="Delivered not invoiced" value={formatCurrency(d.delivered_not_invoiced.estimated_net_value)} />
          <Statistic title="DNI lines" value={d.delivered_not_invoiced.lines_with_availability} />
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>
        Delivered not invoiced
      </Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={d.delivered_not_invoiced.rows}
        pagination={false}
        columns={[
          { title: 'DN #', dataIndex: 'delivery_note_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Delivery date', dataIndex: 'delivery_date' },
          {
            title: '',
            key: 'link',
            render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a>,
          },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Orders at risk
      </Title>
      <Table
        size="small"
        rowKey="sales_order_id"
        dataSource={d.orders_at_risk}
        pagination={{ pageSize: 10 }}
        columns={[
          { title: 'SO #', dataIndex: 'sales_order_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Required', dataIndex: 'required_date', width: 110 },
          { title: 'Readiness', dataIndex: 'readiness_status' },
          {
            title: '',
            key: 'ops',
            width: 120,
            render: (_: unknown, r) => (
              <a onClick={() => navigate('/growth/dashboard?tab=operations#orders-at-risk')}>Operations</a>
            ),
          },
        ]}
      />
    </div>
  );
};
